import type {
  ActiveSubscription,
  PopStoreSubscription,
  Subscriptions,
} from '@ps/models/lib/creator-subscription';
import {
  PopStoreExtendedSubscriptionLevel,
  PopStoreExtendedUserAccessLevel,
  PopStoreSubscriptionStatus,
  PopStoreUserAccessLevel,
} from '@ps/models/lib/creator-subscription';

/**
 * The access levels ordered from least to most privileged, including the free
 * FOLLOWER tier. A viewer at index `i` can access content requiring any level at
 * index `<= i`. Mirrors PopshopRN's `kOrderedExtendedUserAccessLevels`.
 */
export const kOrderedExtendedUserAccessLevels: PopStoreExtendedUserAccessLevel[] =
  [
    PopStoreExtendedUserAccessLevel.ALL,
    PopStoreExtendedUserAccessLevel.FOLLOWER,
    PopStoreExtendedUserAccessLevel.SUBSCRIBER,
    PopStoreExtendedUserAccessLevel.FAN,
    PopStoreExtendedUserAccessLevel.VIP,
  ];

/**
 * Compares two access levels by rank in {@link kOrderedExtendedUserAccessLevels}.
 * `> 0` means `a` is more privileged than `b`.
 */
export function compareExtendedUserAccessLevel(
  a: PopStoreExtendedUserAccessLevel,
  b: PopStoreExtendedUserAccessLevel,
): number {
  return (
    kOrderedExtendedUserAccessLevels.indexOf(a) -
    kOrderedExtendedUserAccessLevels.indexOf(b)
  );
}

/**
 * The paid subscription tiers and the content access levels carry identical
 * string values; these maps make the widening into the extended access space
 * explicit and type-safe instead of a cast.
 */
const EXTENDED_SUBSCRIPTION_TO_ACCESS: Record<
  PopStoreExtendedSubscriptionLevel,
  PopStoreExtendedUserAccessLevel
> = {
  [PopStoreExtendedSubscriptionLevel.FOLLOWER]:
    PopStoreExtendedUserAccessLevel.FOLLOWER,
  [PopStoreExtendedSubscriptionLevel.SUBSCRIBER]:
    PopStoreExtendedUserAccessLevel.SUBSCRIBER,
  [PopStoreExtendedSubscriptionLevel.FAN]: PopStoreExtendedUserAccessLevel.FAN,
  [PopStoreExtendedSubscriptionLevel.VIP]: PopStoreExtendedUserAccessLevel.VIP,
};

const USER_ACCESS_TO_EXTENDED: Record<
  PopStoreUserAccessLevel,
  PopStoreExtendedUserAccessLevel
> = {
  [PopStoreUserAccessLevel.ALL]: PopStoreExtendedUserAccessLevel.ALL,
  [PopStoreUserAccessLevel.SUBSCRIBER]:
    PopStoreExtendedUserAccessLevel.SUBSCRIBER,
  [PopStoreUserAccessLevel.FAN]: PopStoreExtendedUserAccessLevel.FAN,
  [PopStoreUserAccessLevel.VIP]: PopStoreExtendedUserAccessLevel.VIP,
};

/** Widen a required content access level into the extended access space. */
export function userAccessLevelToExtended(
  level: PopStoreUserAccessLevel,
): PopStoreExtendedUserAccessLevel {
  return USER_ACCESS_TO_EXTENDED[level];
}

/**
 * Whether a viewer at `userSubscriptionLevel` may access content requiring
 * `contentAccessLevel` (both in the extended space that includes FOLLOWER).
 * Ported from PopshopRN's `isPopStoreExtendedUserAccessPermitted`:
 *
 * - unrestricted content (`null`) is always permitted;
 * - a viewer with no standing may see only `ALL` content;
 * - otherwise the viewer's level must rank at or above the required level (so a
 *   higher tier grants access to everything a lower tier does).
 */
export function isPopStoreExtendedUserAccessPermitted(
  contentAccessLevel: PopStoreExtendedUserAccessLevel | undefined,
  userSubscriptionLevel: PopStoreExtendedSubscriptionLevel | undefined,
): boolean {
  if (contentAccessLevel == null) return true;
  if (!userSubscriptionLevel) {
    return contentAccessLevel === PopStoreExtendedUserAccessLevel.ALL;
  }
  return (
    compareExtendedUserAccessLevel(
      EXTENDED_SUBSCRIPTION_TO_ACCESS[userSubscriptionLevel],
      contentAccessLevel,
    ) >= 0
  );
}

/**
 * Flattens the `Subscriptions` keyed-by-level container into a list,
 * preserving placeholder entries so callers can render "(not
 * configured)" rows alongside active ones.
 */
export function subscriptionsToList(
  subscriptions: Subscriptions | undefined,
): PopStoreSubscription[] {
  return subscriptions ? Object.values(subscriptions) : [];
}

/**
 * `true` if the creator has finished configuring this tier (regardless
 * of whether the subscription is currently sellable).
 */
export function isActiveSubscription(
  subscription: PopStoreSubscription,
): subscription is ActiveSubscription {
  return (
    subscription.subscription_configuration_status ===
    PopStoreSubscriptionStatus.Active
  );
}

/**
 * `true` if the tier is configured AND currently sellable
 * (`creator_subscription.active !== false`).
 *
 * Inactive-but-configured tiers can still be shown as a hint but
 * shouldn't be selectable.
 */
export function isAvailableSubscription(
  subscription: PopStoreSubscription,
): subscription is ActiveSubscription {
  return (
    subscription.subscription_configuration_status ===
      PopStoreSubscriptionStatus.Active &&
    subscription.subscription_data.creator_subscription.active !== false
  );
}

export interface SubscriptionIdentifier {
  platform_sub_level_id: number;
  creator_sub_id: number;
}

export function activeSubscriptionIdentifier(
  subscription: ActiveSubscription,
): SubscriptionIdentifier {
  return {
    platform_sub_level_id:
      subscription.subscription_data.platform_subscription_level
        .platform_sub_level_id,
    creator_sub_id:
      subscription.subscription_data.creator_subscription.creator_sub_id,
  };
}
